/**
 * Session Storage utilities for Tenant Setup
 * Handles persisting setup data across browser sessions
 * Data is automatically cleared when the browser session ends
 */

const STORAGE_KEY = "tenantSetupFormData";

/**
 * Initialize or get the entire setup data from session storage
 * @returns {object} The complete setup data object
 */
export const getSetupData = () => {
  try {
    const data = sessionStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : { step1: {}, step2: {} };
  } catch (error) {
    console.error("Error reading from session storage:", error);
    return { step1: {}, step2: {} };
  }
};

/**
 * Update data for a specific step
 * @param {string} step - The step name ('step1' or 'step2')
 * @param {object} data - The data to save for that step
 */
export const updateStepData = (step, data) => {
  try {
    const currentData = getSetupData();
    const updatedData = {
      ...currentData,
      [step]: { ...currentData[step], ...data },
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updatedData));
  } catch (error) {
    console.error("Error writing to session storage:", error);
  }
};

/**
 * Get data for a specific step
 * @param {string} step - The step name ('step1' or 'step2')
 * @returns {object} The data for that step
 */
export const getStepData = (step) => {
  try {
    const data = getSetupData();
    return data[step] || {};
  } catch (error) {
    console.error("Error reading step data from session storage:", error);
    return {};
  }
};

/**
 * Clear all setup data from session storage
 * This is called on successful setup completion
 */
export const clearSetupData = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing session storage:", error);
  }
};
